import { create } from 'zustand';
import { useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X } from '../../../icons';
import './Toast.css';

export type ToastVariant = 'success' | 'error' | 'info';

interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
  /** unmount transition 단계 — true 시 slide-out animation 진행 중. */
  leaving?: boolean;
}

interface ToastState {
  items: ToastItem[];
  push: (variant: ToastVariant, message: string) => void;
  /** 2단계 dismiss — leaving 표시 → 200ms 후 실 제거. modal unmount cycle 의
   *  pattern-modal-leave-hook-latched-position-tree 의 *queue 변형*. */
  dismiss: (id: number) => void;
}

let nextId = 1;
const LEAVE_MS = 200;
const AUTO_DISMISS_MS = 4000;

export const useToastStore = create<ToastState>((set, get) => ({
  items: [],
  push: (variant, message) => {
    const id = nextId++;
    set({ items: [...get().items, { id, variant, message }] });
    setTimeout(() => get().dismiss(id), AUTO_DISMISS_MS);
  },
  dismiss: (id) => {
    const target = get().items.find((i) => i.id === id);
    if (!target || target.leaving) return; // 이미 dismiss 진행 중 — race 봉합
    // 1단계: leaving=true 로 변경 → CSS slide-out animation trigger
    set({ items: get().items.map((i) => (i.id === id ? { ...i, leaving: true } : i)) });
    // 2단계: LEAVE_MS 후 array 에서 실 제거
    setTimeout(() => set({ items: get().items.filter((i) => i.id !== id) }), LEAVE_MS);
  },
}));

export const toast = {
  success: (m: string) => useToastStore.getState().push('success', m),
  error: (m: string) => useToastStore.getState().push('error', m),
  info: (m: string) => useToastStore.getState().push('info', m),
};

export function ToastViewport() {
  const items = useToastStore((s) => s.items);
  const dismiss = useToastStore((s) => s.dismiss);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && items[0]) dismiss(items[0].id);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [items, dismiss]);

  if (items.length === 0) return null;

  return (
    <div className="toast-viewport" role="status" aria-live="polite">
      {items.map((t) => {
        const Icon = t.variant === 'success' ? CheckCircle : t.variant === 'error' ? AlertCircle : Info;
        return (
          <div
            key={t.id}
            className={`toast toast-${t.variant}${t.leaving ? ' toast--leaving' : ''}`}
            aria-hidden={t.leaving || undefined}
          >
            <Icon size={16} />
            <span>{t.message}</span>
            <button
              type="button"
              onClick={() => dismiss(t.id)}
              aria-label="dismiss"
              disabled={t.leaving}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
